import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useVenueDateTime from "../hooks/useVenueDateTime";
import { useTranslation } from "i18n-fe";
import { formatPoints, entryMethodOf } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { buildMovementDiffRows } from "../util/contenderCompare";
import { ENTRY_METHOD_DISPLAY } from "./EntryMethodChip";

// #2816 (scribe-resilience G3): the scoring tent's side-by-side view of two
// contender sheets for one judge seat (AC1/AC2), with the one-click switch
// action (AC3/AC4, server-side in apps/score-api/src/handlers/scoresheet.js
// switchAuthoritativeScoreSheet). Modeled on SheetAuditDialog.jsx's shape
// (same fmt() venue-local timestamp helper, same MUI Dialog/Table posture),
// but reads its data straight off the `sheets` prop RideEditor already has
// in state, no extra fetch, since listScoreSheets already returns full
// sheet objects including movementScores/provenance/authoritative/
// contenderOf.

const EMPTY_VALUE = "–"; // en dash placeholder for a missing value, not an em dash.

// Same posture as SheetAuditDialog's fmt(): a genuine server-stamped instant
// (ScoreSheet.provenance.capturedAt is `new Date().toISOString()` on create,
// never an FVD Access stamp), so it renders venue-local on the ACTIVE SHOW's
// zone (with the zone label beside it), never the viewer's browser timezone,
// and says "Venue timezone not set" rather than a silent Pacific time (#2097).
const COMPARE_DATETIME = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};

function useFmt() {
  const format = useVenueDateTime(COMPARE_DATETIME);
  return (at) => (at ? format(at) : EMPTY_VALUE);
}

function ProvenanceColumn({ sheet, isAuthoritative, t }) {
  const fmt = useFmt();
  const provenance = sheet.provenance ?? {};
  // #1931 — the entry method beside the #2815 origin/author/device rows:
  // which contender's marks were keyed live vs from paper vs reconstructed
  // is exactly the distinction a human comparing two same-seat sheets needs.
  // Absent method (pre-#1931 sheet) renders the column's own "Unknown", same
  // as every other missing provenance field here — never scribe-live (AC4).
  const method = entryMethodOf(sheet);
  return (
    <Stack spacing={0.5} sx={{ mb: 1 }} data-testid={`contender-compare-column-${sheet.id}`}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color={isAuthoritative ? "success" : "default"}
          variant={isAuthoritative ? "filled" : "outlined"}
          data-testid={`contender-compare-status-chip-${sheet.id}`}
          label={t(
            isAuthoritative
              ? "contenderCompare.authoritativeChip"
              : "contenderCompare.contenderChip"
          )}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-origin-${sheet.id}`}>
        {t("contenderCompare.originLabel")} {provenance.origin || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-method-${sheet.id}`}>
        {t("contenderCompare.methodLabel")}{" "}
        {method ? t(ENTRY_METHOD_DISPLAY[method].labelKey) : t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-author-${sheet.id}`}>
        {t("contenderCompare.authorLabel")} {provenance.author || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-device-${sheet.id}`}>
        {t("contenderCompare.deviceLabel")} {provenance.device || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-capturedat-${sheet.id}`}>
        {t("contenderCompare.capturedAtLabel")} {fmt(provenance.capturedAt)}
      </Typography>
    </Stack>
  );
}

function MarkCell({ mark }) {
  if (!mark) return <TableCell>{EMPTY_VALUE}</TableCell>;
  return <TableCell>{mark.score ?? EMPTY_VALUE}</TableCell>;
}

// #2986: buildMovementDiffRows can flag a row as `differs` purely on a
// non-score field (deductions/flagged/remarks/annotated*) that MarkCell
// never renders -- the row lit up warning-color with two identical-looking
// score cells and no visible reason. Names ONLY the non-score fields so the
// caption never duplicates what a genuine score difference already shows in
// the cells themselves.
const DIFF_FIELD_LABEL_KEYS = {
  deductions: "contenderCompare.diffField.deductions",
  flagged: "contenderCompare.diffField.flagged",
  remarks: "contenderCompare.diffField.remarks",
  annotatedDirective: "contenderCompare.diffField.annotatedDirective",
  annotatedDescription: "contenderCompare.diffField.annotatedDescription",
};

function nonScoreDiffFields(differingFields) {
  return (differingFields ?? []).filter((f) => f !== "score");
}

// #3364: name a sheet's test for the mismatch banner. Same fallback chain as
// TestDisagreementChip's testLabel (name, then testCode, then the raw id) so a
// test def that failed to load still names SOMETHING rather than vanishing.
function testLabelFor(testId, testsById) {
  if (!testId) return EMPTY_VALUE;
  return testsById?.[testId]?.name || testsById?.[testId]?.testCode || testId;
}

// #3364: true when the contending sheets were scored on more than one test.
// Derived from each sheet's OWN testId (never from the audit trail), so it
// holds for any cause, not only a confirmed cross-test repoint. A sheet with
// no testId says nothing either way, so it is ignored. Pure display flag:
// nothing here ranks the sheets or picks a winner.
function contenderTestsDiffer(sheets) {
  return new Set((sheets ?? []).map((s) => s.testId).filter(Boolean)).size > 1;
}

export default function ContenderCompareDialog({ open, onClose, judgeId, sheets, test, tests, canSwitch, onSwitched }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [confirmSheetId, setConfirmSheetId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const rows = buildMovementDiffRows(sheets);
  const testsDiffer = contenderTestsDiffer(sheets);

  async function handleConfirmSwitch() {
    if (!confirmSheetId) return;
    try {
      setSwitching(true);
      setError(null);
      const updated = unwrap(
        await scoreApi.scoreSheets.switchAuthoritativeScoreSheet(confirmSheetId)
      );
      onSwitched?.(updated);
      setConfirmSheetId(null);
      onClose?.();
    } catch (err) {
      setError(err);
    } finally {
      setSwitching(false);
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("contenderCompare.title", { judgeId: judgeId || EMPTY_VALUE })}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("contenderCompare.switchError")}
            </Alert>
          )}
          {/* #3364: after a confirmed cross-test repoint (#3354) a moved sheet
              can contend with a sheet scored on a DIFFERENT test. Name both
              tests so the tent sees the movements are not comparable. This
              only informs: no winner is computed or suggested. */}
          {testsDiffer && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="contender-compare-test-mismatch">
              {t("contenderCompare.testMismatchBody")}
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {(sheets ?? []).map((sheet, idx) => (
                  <li key={sheet.id} data-testid={`contender-compare-test-mismatch-test-${sheet.id}`}>
                    {t(idx === 0 ? "contenderCompare.authoritativeChip" : "contenderCompare.contenderChip")}
                    {": "}
                    {testLabelFor(sheet.testId, tests)}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            {(sheets ?? []).map((sheet, idx) => (
              <Box key={sheet.id} sx={{ flex: 1 }}>
                <ProvenanceColumn sheet={sheet} isAuthoritative={idx === 0} t={t} />
                {/* AC4: a principal without switch_authoritative_score_sheet
                    (a scribe) gets no button at all here, not a disabled
                    one: canSwitch is a token-roles check, never a UI-only
                    disable, so there is nothing to click even via devtools
                    that the server wouldn't also reject. */}
                {idx !== 0 && canSwitch && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirmSheetId(sheet.id)}
                    data-testid="contender-compare-switch-button"
                  >
                    {t("contenderCompare.switchButton")}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("contenderCompare.movementColumn")}</TableCell>
                  {(sheets ?? []).map((sheet, idx) => (
                    <TableCell key={sheet.id}>
                      {idx === 0
                        ? t("contenderCompare.authoritativeChip")
                        : t("contenderCompare.contenderChip")}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const nonScoreFields = nonScoreDiffFields(row.differingFields);
                  return (
                    <TableRow
                      key={row.num}
                      data-testid="contender-compare-row"
                      sx={
                        row.differs
                          ? { bgcolor: "warning.main", "& *": { color: "warning.contrastText" } }
                          : undefined
                      }
                    >
                      <TableCell>
                        {row.num}
                        {/* #2986 AC1: a row can be highlighted by a field this
                            table never renders per-cell (deductions/flagged/
                            remarks/annotated*) -- e.g. equal scores, different
                            remarks. Name it here so the highlight is never
                            unexplained. Score differences stay self-evident
                            in the cells and are deliberately not repeated. */}
                        {nonScoreFields.length > 0 && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="inherit"
                            data-testid="contender-compare-row-diff-hint"
                          >
                            {t("contenderCompare.differsInLabel")}{" "}
                            {nonScoreFields.map((f) => t(DIFF_FIELD_LABEL_KEYS[f])).join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      {row.marks.map((mark, idx) => (
                        <MarkCell key={sheets?.[idx]?.id ?? idx} mark={mark} />
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {(sheets ?? []).map((sheet, idx) => (
              <span key={sheet.id}>
                {idx > 0 ? " · " : ""}
                {t("contenderCompare.totalLabel")} {formatPoints(sheet.totalPoints, test)} /{" "}
                {formatPoints(sheet.maxPoints, test)}
              </span>
            ))}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmSheetId)} onClose={() => setConfirmSheetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("contenderCompare.switchConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("contenderCompare.switchConfirmBody", { judgeId: judgeId || EMPTY_VALUE })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSheetId(null)} disabled={switching}>
            {t("contenderCompare.cancel")}
          </Button>
          <Button
            onClick={handleConfirmSwitch}
            loading={switching}
            variant="contained"
            data-testid="contender-compare-confirm-switch"
          >
            {t("contenderCompare.confirmSwitch")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
